/**
 * O que as cinco listagens do credenciamento e da frota tem em comum.
 *
 * Os nomes dos campos sao os das COLUNAS DO BANCO, em minusculas com sublinhado —
 * mesma decisao do contrato do conector gov.br
 * (modulos/admin/integracoes/contrato.ts): e assim que a API responde, e reescrever
 * para camelCase criaria uma traducao a mais para manter em dia sem ganhar nada.
 *
 * A fonte da verdade destes tipos e `docs/MODELO-DE-DADOS.md` e as migracoes que
 * criaram as tabelas (`db/migrations/`). Este arquivo e o espelho delas aqui.
 */

import type { StatusVariant } from '@/ui/StatusBadge';

/** Paginacao do servidor, no mesmo formato do resto do sistema. */
export interface Pagina<T> {
  itens: T[];
  total: number;
  pagina: number;
  tamanho: number;
}

/**
 * De onde o registro veio.
 *
 * Titular, condutor, monitor e veiculo do TEG nascem, em regra, de integracao com o
 * SGTP. As duas formas manuais existem porque a integracao cai, e a operacao nao
 * pode parar: `MANUAL_CONTINGENCIA` e cadastro feito com a integracao fora do ar;
 * `MANUAL_ASSISTIDO` e cadastro feito com decisao humana registrada, quando a
 * integracao responde e o dado dela e que esta errado.
 *
 * A distincao importa na hora de conferir: contingencia se reconcilia quando o SGTP
 * volta, assistido nao — ele tem parecer por tras.
 */
export const ORIGENS_DO_DADO = [
  'INTEGRACAO_SGTP',
  'MANUAL_CONTINGENCIA',
  'MANUAL_ASSISTIDO',
  'SISTEMA_TEG',
] as const;

export type OrigemDoDado = (typeof ORIGENS_DO_DADO)[number];

export const ROTULO_DA_ORIGEM: Record<OrigemDoDado, string> = {
  INTEGRACAO_SGTP: 'SGTP',
  MANUAL_CONTINGENCIA: 'Contingência',
  MANUAL_ASSISTIDO: 'Assistido',
  SISTEMA_TEG: 'Sistema TEG',
};

export const EXPLICACAO_DA_ORIGEM: Record<OrigemDoDado, string> = {
  INTEGRACAO_SGTP: 'Dado recebido da integração com o SGTP.',
  MANUAL_CONTINGENCIA: 'Cadastro manual feito com a integração indisponível.',
  MANUAL_ASSISTIDO: 'Cadastro manual com decisão registrada por quem o fez.',
  SISTEMA_TEG: 'Alteração feita dentro do próprio Sistema TEG.',
};

/** Opcoes de origem para o filtro das listagens que expoem esse recorte. */
export const OPCOES_DE_ORIGEM: readonly { value: OrigemDoDado; label: string }[] = [
  { value: 'INTEGRACAO_SGTP', label: ROTULO_DA_ORIGEM.INTEGRACAO_SGTP },
  { value: 'MANUAL_CONTINGENCIA', label: ROTULO_DA_ORIGEM.MANUAL_CONTINGENCIA },
  { value: 'MANUAL_ASSISTIDO', label: ROTULO_DA_ORIGEM.MANUAL_ASSISTIDO },
];

/**
 * Situacao cadastral das pessoas e dos veiculos.
 *
 * `BLOQUEADO` so existe em credenciado — e consequencia de pendencia financeira ou
 * administrativa, e nao se aplica a um condutor. Por isso o tipo mais estreito
 * existe separado, em vez de todos usarem a lista longa e cada tela lidar com um
 * valor que nunca vai chegar.
 */
export const SITUACOES_DA_PESSOA = ['ATIVO', 'INATIVO', 'SUSPENSO'] as const;
export type SituacaoDaPessoa = (typeof SITUACOES_DA_PESSOA)[number];

export const SITUACOES_DO_CREDENCIADO = ['ATIVO', 'INATIVO', 'SUSPENSO', 'BLOQUEADO'] as const;
export type SituacaoDoCredenciado = (typeof SITUACOES_DO_CREDENCIADO)[number];

export const OPCOES_DE_SITUACAO_DA_PESSOA: readonly { value: SituacaoDaPessoa; label: string }[] = [
  { value: 'ATIVO', label: 'Ativo' },
  { value: 'INATIVO', label: 'Inativo' },
  { value: 'SUSPENSO', label: 'Suspenso' },
];

export const OPCOES_DE_SITUACAO_DO_CREDENCIADO: readonly {
  value: SituacaoDoCredenciado;
  label: string;
}[] = [
  { value: 'ATIVO', label: 'Ativo' },
  { value: 'INATIVO', label: 'Inativo' },
  { value: 'SUSPENSO', label: 'Suspenso' },
  { value: 'BLOQUEADO', label: 'Bloqueado' },
];

/**
 * Uma diretoria regional de ensino, como o filtro precisa dela.
 *
 * `public.dres` tem so `codigo` e `nome` (docs/ALVOS.md, Onda 2). Nao ha mais nada a
 * pedir, e nao se inventa campo aqui para a tela ficar mais cheia.
 */
export interface Dre {
  id: string;
  codigo: string;
  nome: string;
}

/** Credenciado reduzido, como ele aparece pendurado num condutor, monitor ou veiculo. */
export interface CredenciadoResumido {
  id: string;
  slug: string;
  nome: string;
  cpf_cnpj: string;
  tipo?: string | null;
  dres?: Pick<Dre, 'codigo' | 'nome'> | null;
}

/**
 * Campos de integracao que quatro das cinco entidades carregam.
 *
 * `em_contingencia` NAO e o mesmo que `origem_dado === 'MANUAL_CONTINGENCIA'`: o
 * primeiro diz que o registro esta AGORA fora de sincronia e precisa reconciliar; o
 * segundo diz como ele nasceu. Um registro criado em contingencia e depois
 * reconciliado tem origem de contingencia e nao esta mais em contingencia.
 */
export interface CamposDeIntegracao {
  origem_dado: OrigemDoDado | null;
  /** ISO 8601 em UTC, ou nulo quando nunca sincronizou. */
  sincronizado_em: string | null;
  em_contingencia: boolean | null;
}

/** Parametros que toda listagem manda para a API. */
export interface FiltroDeListagem {
  pagina: number;
  tamanho: number;
  busca?: string;
  situacao?: string;
}

/**
 * A situacao como o selo de situacao espera receber.
 *
 * `StatusBadge` tem vocabulario fechado, em minusculas; a coluna do banco e um tipo
 * enumerado em maiusculas. A conversao mora aqui, e nao em `toLowerCase() as
 * StatusVariant` espalhado por cinco telas — que e justamente a forma de um valor novo
 * do banco virar selo sem cor em quatro telas e colorido na quinta.
 *
 * Valor desconhecido cai em `rascunho` (cinza neutro) em vez de estourar: um tipo
 * enumerado novo no banco nao pode derrubar a listagem inteira.
 */
export function situacaoComoSelo(situacao: string): StatusVariant {
  const normalizada = situacao.toLowerCase();
  const conhecidas: readonly StatusVariant[] = ['ativo', 'inativo', 'suspenso', 'bloqueado'];
  return conhecidas.includes(normalizada as StatusVariant)
    ? (normalizada as StatusVariant)
    : 'rascunho';
}

/**
 * Monta a query de uma listagem sem despejar parametro vazio no endereco.
 *
 * `?busca=&situacao=` nao diz nada que a ausencia ja nao diga, e polui o log da API e
 * a chave de cache — duas chaves diferentes para a mesma consulta significa cache que
 * nunca acerta.
 */
export function parametrosDaListagem(filtro: Record<string, unknown>): string {
  const parametros = new URLSearchParams();

  for (const [chave, valor] of Object.entries(filtro)) {
    if (valor === undefined || valor === null || valor === '') {
      continue;
    }
    if (Array.isArray(valor)) {
      if (valor.length > 0) {
        parametros.set(chave, valor.join(','));
      }
      continue;
    }
    parametros.set(chave, String(valor));
  }

  const texto = parametros.toString();
  return texto ? `?${texto}` : '';
}
